
import { useState } from 'react';

type ServiceCategory = '' | 'legalization' | 'oldhousing' | 'solar';

interface FormFields {
  name: string;
  phone: string;
  address: string;
  category: ServiceCategory;
  buildingType: string;
  violationType: string;
  violationDetail: string;
  housingAge: string;
  housingIssue: string;
  housingArea: string;
  roofArea: string;
  roofCondition: string;
  electricUsage: string;
  message: string;
}

const initialFormData: FormFields = {
  name: '',
  phone: '',
  address: '',
  category: '',
  buildingType: '',
  violationType: '',
  violationDetail: '',
  housingAge: '',
  housingIssue: '',
  housingArea: '',
  roofArea: '',
  roofCondition: '',
  electricUsage: '',
  message: '',
};

export default function ConsultationForm() {
  const [formData, setFormData] = useState<FormFields>({ ...initialFormData });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const categoryLabel = (cat: ServiceCategory) => {
    switch (cat) {
      case 'legalization':
        return '불법건축물 양성화';
      case 'oldhousing':
        return '노후주택 개선';
      case 'solar':
        return '태양광 설치';
      default:
        return '';
    }
  };

  const categoryIcon = (cat: ServiceCategory) => {
    switch (cat) {
      case 'legalization':
        return 'ri-building-2-line';
      case 'oldhousing':
        return 'ri-home-gear-line';
      case 'solar':
        return 'ri-sun-line';
      default:
        return '';
    }
  };

  const handleCategorySelect = (cat: ServiceCategory) => {
    setFormData({
      ...initialFormData,
      name: formData.name,
      phone: formData.phone,
      address: formData.address,
      category: cat,
    });
    setIsCategoryOpen(false);
    setSubmitStatus('idle');
  };

  const update = (field: keyof FormFields, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.category) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    let submitUrl = '';
    if (formData.category === 'legalization') {
      submitUrl = 'https://readdy.ai/api/form/d6bb5edbd17lru29maq0';
    } else if (formData.category === 'oldhousing') {
      submitUrl = 'https://readdy.ai/api/form/d6bb5edbd17lru29maqg';
    } else {
      submitUrl = 'https://readdy.ai/api/form/d6bb96tbiueuoqm1t1cg';
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('address', formData.address);
      formBody.append('serviceType', categoryLabel(formData.category));

      if (formData.category === 'legalization') {
        formBody.append('buildingType', formData.buildingType);
        formBody.append('violationType', formData.violationType);
        formBody.append('violationDetail', formData.violationDetail);
      } else if (formData.category === 'oldhousing') {
        formBody.append('buildingType', formData.buildingType);
        formBody.append('housingAge', formData.housingAge);
        formBody.append('housingIssue', formData.housingIssue);
        formBody.append('housingArea', formData.housingArea);
      } else if (formData.category === 'solar') {
        formBody.append('buildingType', formData.buildingType);
        formBody.append('roofArea', formData.roofArea);
        formBody.append('roofCondition', formData.roofCondition);
        formBody.append('electricUsage', formData.electricUsage);
      }

      formBody.append('message', formData.message);

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ ...initialFormData });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2C4A3E] focus:border-transparent outline-none transition-all text-sm bg-white';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-2';
  const required = <span className="text-red-500 ml-0.5">*</span>;

  const renderLegalizationFields = () => (
    <>
      {/* Building Type */}
      <div className="mb-5">
        <label className={labelClass}>건물 종류 {required}</label>
        <select
          name="buildingType"
          value={formData.buildingType}
          onChange={(e) => update('buildingType', e.target.value)}
          required
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">선택해주세요</option>
          <option value="단독주택">단독주택</option>
          <option value="다가구주택">다가구주택</option>
          <option value="다세대주택">다세대주택</option>
          <option value="빌라">빌라</option>
          <option value="근린생활시설">근린생활시설</option>
          <option value="상가주택">상가주택</option>
          <option value="기타">기타</option>
        </select>
      </div>

      {/* Violation Type */}
      <div className="mb-5">
        <label className={labelClass}>위반 유형 {required}</label>
        <select
          name="violationType"
          value={formData.violationType}
          onChange={(e) => update('violationType', e.target.value)}
          required
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">선택해주세요</option>
          <option value="옥탑방 증축 (샌드위치 판넬)">옥탑방 증축 (샌드위치 판넬)</option>
          <option value="베란다/발코니 무단 확장">베란다/발코니 무단 확장</option>
          <option value="근생시설의 주택 사용 (근생빌라)">근생시설의 주택 사용 (근생빌라)</option>
          <option value="방 쪼개기 (가구 분할)">방 쪼개기 (가구 분할)</option>
          <option value="무허가 증축">무허가 증축</option>
          <option value="기타">기타</option>
        </select>
      </div>

      {/* Violation Detail */}
      <div className="mb-5">
        <label className={labelClass}>위반 상세 내용 {required}</label>
        <input
          type="text"
          name="violationDetail"
          value={formData.violationDetail}
          onChange={(e) => update('violationDetail', e.target.value)}
          required
          className={inputClass}
          placeholder="예: 옥상에 판넬로 방 2개 증축, 약 30㎡"
        />
      </div>
    </>
  );

  const renderOldhousingFields = () => (
    <>
      {/* Building Type */}
      <div className="mb-5">
        <label className={labelClass}>건물 종류 {required}</label>
        <select
          name="buildingType"
          value={formData.buildingType}
          onChange={(e) => update('buildingType', e.target.value)}
          required
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">선택해주세요</option>
          <option value="단독주택">단독주택</option>
          <option value="다가구주택">다가구주택</option>
          <option value="다세대주택">다세대주택</option>
          <option value="아파트">아파트</option>
          <option value="빌라">빌라</option>
          <option value="기타">기타</option>
        </select>
      </div>

      {/* Housing Age */}
      <div className="mb-5">
        <label className={labelClass}>건축 경과 연수 {required}</label>
        <select
          name="housingAge"
          value={formData.housingAge}
          onChange={(e) => update('housingAge', e.target.value)}
          required
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">선택해주세요</option>
          <option value="15년~20년">15년~20년</option>
          <option value="20년~30년">20년~30년</option>
          <option value="30년~40년">30년~40년</option>
          <option value="40년 이상">40년 이상</option>
        </select>
      </div>

      {/* Housing Issue */}
      <div className="mb-5">
        <label className={labelClass}>주요 문제점 {required}</label>
        <select
          name="housingIssue"
          value={formData.housingIssue}
          onChange={(e) => update('housingIssue', e.target.value)}
          required
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">선택해주세요</option>
          <option value="지붕 누수/파손">지붕 누수/파손</option>
          <option value="외벽 균열/노후">외벽 균열/노후</option>
          <option value="배관 노후/누수">배관 노후/누수</option>
          <option value="단열 불량">단열 불량</option>
          <option value="전기 설비 노후">전기 설비 노후</option>
          <option value="창호 교체 필요">창호 교체 필요</option>
          <option value="종합 개선 필요">종합 개선 필요</option>
        </select>
      </div>

      {/* Housing Area */}
      <div className="mb-5">
        <label className={labelClass}>주택 면적 {required}</label>
        <input
          type="text"
          name="housingArea"
          value={formData.housingArea}
          onChange={(e) => update('housingArea', e.target.value)}
          required
          className={inputClass}
          placeholder="예: 약 85㎡ (25평)"
        />
      </div>
    </>
  );

  const renderSolarFields = () => (
    <>
      {/* Building Type */}
      <div className="mb-5">
        <label className={labelClass}>건물 종류 {required}</label>
        <select
          name="buildingType"
          value={formData.buildingType}
          onChange={(e) => update('buildingType', e.target.value)}
          required
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">선택해주세요</option>
          <option value="단독주택">단독주택</option>
          <option value="다가구주택">다가구주택</option>
          <option value="다세대주택">다세대주택</option>
          <option value="빌라">빌라</option>
          <option value="상가건물">상가건물</option>
          <option value="공장/창고">공장/창고</option>
          <option value="기타">기타</option>
        </select>
      </div>

      {/* Roof Area */}
      <div className="mb-5">
        <label className={labelClass}>옥상 면적 {required}</label>
        <input
          type="text"
          name="roofArea"
          value={formData.roofArea}
          onChange={(e) => update('roofArea', e.target.value)}
          required
          className={inputClass}
          placeholder="예: 약 50㎡"
        />
      </div>

      {/* Roof Condition */}
      <div className="mb-5">
        <label className={labelClass}>옥상 상태 {required}</label>
        <select
          name="roofCondition"
          value={formData.roofCondition}
          onChange={(e) => update('roofCondition', e.target.value)}
          required
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">선택해주세요</option>
          <option value="평평한 옥상 (평슬라브)">평평한 옥상 (평슬라브)</option>
          <option value="경사 지붕">경사 지붕</option>
          <option value="옥상 방수 양호">옥상 방수 양호</option>
          <option value="옥상 방수 불량">옥상 방수 불량</option>
          <option value="기존 구조물 있음">기존 구조물 있음</option>
        </select>
      </div>

      {/* Electric Usage */}
      <div className="mb-5">
        <label className={labelClass}>월 평균 전기 사용량 {required}</label>
        <select
          name="electricUsage"
          value={formData.electricUsage}
          onChange={(e) => update('electricUsage', e.target.value)}
          required
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">선택해주세요</option>
          <option value="100kWh 이하">100kWh 이하</option>
          <option value="100~200kWh">100~200kWh</option>
          <option value="200~350kWh">200~350kWh</option>
          <option value="350~500kWh">350~500kWh</option>
          <option value="500kWh 이상">500kWh 이상</option>
          <option value="잘 모르겠음">잘 모르겠음</option>
        </select>
      </div>
    </>
  );

  const categories: { value: ServiceCategory; label: string; desc: string }[] = [
    { value: 'legalization', label: '불법건축물 양성화', desc: '2026 특별법 기반 합법화 진단' },
    { value: 'oldhousing', label: '노후주택 개선', desc: '15년 이상 주택 정부 지원 사업' },
    { value: 'solar', label: '태양광 설치', desc: '옥상 태양광 패널 설치 지원' },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-[#2C4A3E] to-[#1f3329] relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-64 h-64 border border-white rounded-full"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 border border-white rounded-full"></div>
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">무료 사전 진단 신청하기</h2>
          <p className="text-lg text-gray-300">
            2026년 특별법은 단 1년만 시행됩니다. 신청 기간이 짧아 접수가 폭주할 수 있으니
            <br />
            지금 미리 서류를 준비해야 성공 확률이 높습니다.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-3xl mx-auto">
          <form id="consultation-form" data-readdy-form onSubmit={handleSubmit}>
            {/* Name & Phone */}
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className={labelClass}>이름 {required}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => update('name', e.target.value)}
                  required
                  className={inputClass}
                  placeholder="홍길동"
                />
              </div>
              <div>
                <label className={labelClass}>연락처 {required}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  required
                  className={inputClass}
                  placeholder="010-1234-5678"
                />
              </div>
            </div>

            {/* Address */}
            <div className="mb-5">
              <label className={labelClass}>주소 {required}</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={(e) => update('address', e.target.value)}
                required
                className={inputClass}
                placeholder="서울시 강남구 테헤란로 123"
              />
            </div>

            {/* Category Selector */}
            <div className="mb-5 relative">
              <label className={labelClass}>분류 {required}</label>
              <button
                type="button"
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`${inputClass} text-left flex items-center justify-between cursor-pointer ${
                  !formData.category ? 'text-gray-400' : 'text-gray-900'
                }`}
              >
                <span className="flex items-center gap-2">
                  {formData.category && (
                    <span className="w-5 h-5 flex items-center justify-center text-[#2C4A3E]">
                      <i className={categoryIcon(formData.category)}></i>
                    </span>
                  )}
                  {formData.category ? categoryLabel(formData.category) : '서비스 분류를 선택해주세요'}
                </span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {/* Dropdown */}
              {isCategoryOpen && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-fadeIn">
                  {categories.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => handleCategorySelect(cat.value)}
                      className={`w-full px-5 py-4 flex items-center gap-4 hover:bg-[#f0f7f4] transition-colors cursor-pointer text-left ${
                        formData.category === cat.value ? 'bg-[#f0f7f4]' : ''
                      }`}
                    >
                      <span
                        className={`w-10 h-10 flex items-center justify-center rounded-full text-lg ${
                          formData.category === cat.value ? 'bg-[#2C4A3E] text-white' : 'bg-gray-100 text-[#2C4A3E]'
                        }`}
                      >
                        <i className={categoryIcon(cat.value)}></i>
                      </span>
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{cat.label}</div>
                        <div className="text-xs text-gray-500">{cat.desc}</div>
                      </div>
                      {formData.category === cat.value && (
                        <span className="ml-auto w-5 h-5 flex items-center justify-center text-[#2C4A3E]">
                          <i className="ri-check-line"></i>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dynamic Fields based on Category */}
            {formData.category && (
              <div className="border-t border-gray-100 pt-5 mt-2 animate-slideDown">
                <div className="flex items-center gap-2 mb-5">
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2C4A3E] text-white text-xs">
                    <i className={categoryIcon(formData.category)}></i>
                  </span>
                  <span className="text-sm font-bold text-[#2C4A3E]">{categoryLabel(formData.category)} 상세 정보</span>
                </div>

                {formData.category === 'legalization' && renderLegalizationFields()}
                {formData.category === 'oldhousing' && renderOldhousingFields()}
                {formData.category === 'solar' && renderSolarFields()}
              </div>
            )}

            {/* Message */}
            <div className="mb-6">
              <label className={labelClass}>상세 내용 (선택사항)</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    update('message', e.target.value);
                  }
                }}
                maxLength={500}
                rows={4}
                className={`${inputClass} resize-none`}
                placeholder="추가로 전달하실 내용이 있으시면 작성해주세요 (최대 500자)"
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">{formData.message.length}/500자</div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !formData.category}
              className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-300 whitespace-nowrap ${
                formData.category
                  ? 'bg-[#2C4A3E] text-white hover:bg-[#1f3329] hover:scale-[1.02] cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center animate-spin">
                    <i className="ri-loader-4-line"></i>
                  </span>
                  제출 중...
                </span>
              ) : !formData.category ? (
                '분류를 먼저 선택해주세요'
              ) : (
                '무료 상담 신청하기'
              )}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-center text-sm">
                <span className="w-5 h-5 inline-flex items-center justify-center mr-1">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                상담 신청이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-center text-sm">
                <span className="w-5 h-5 inline-flex items-center justify-center mr-1">
                  <i className="ri-error-warning-line"></i>
                </span>
                신청 중 오류가 발생했습니다. 다시 시도해주세요.
              </div>
            )}
          </form>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center text-white">
          <p className="text-sm opacity-80">
            개인정보는 상담 목적으로만 사용되며, 관련 법령에 따라 안전하게 보호됩니다.
          </p>
        </div>
      </div>

      {/* Inline CSS animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </section>
  );
}
